import { ArrowBack, NavigateNext } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Link,
  Paper,
  styled,
  Typography,
} from "@mui/material";
import DOMPurify from "dompurify";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import how_to_participate from "../../assets/WhatsNewSection/How_to_Participate.png";
import { setActiveAnnouncement, setAnnouncementView, setIsShowAnnouncements } from "../../store/Slice/announcementSlice";
import { getImageURL } from "../../helpers";

const AnnouncementCard = ({ announcement, hasArrow = true, onClick }) => (
  <Card
    sx={{
      height: "100%",
      position: "relative",
      borderRadius: 2,
      overflow: "hidden",
      cursor: onClick ? "pointer" : "default",
    }}
    onClick={onClick}
  >
    <CardMedia
      component="img"
      height="300"
      image={getImageURL(announcement.poster)}
      alt={announcement.eventName}
    />
    {hasArrow && (
      <IconButton
        sx={{
          position: "absolute",
          right: 8,
          bottom: 8,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.9)",
          },
        }}
      >
        <NavigateNext />
      </IconButton>
    )}
  </Card>
);

const PreviewCard = ({ announcement, hasArrow = true, onClick }) => (
  <Card
    sx={{
      height: "100%",
      position: "relative",
      borderRadius: 2,
      overflow: "hidden",
      cursor: onClick ? "pointer" : "default",
    }}
    onClick={onClick}
  >
    <CardMedia
      component="img"
      height="220"
      image={getImageURL(announcement.poster)}
      alt={announcement.eventName}
    />
    {/* <CardContent sx={{ position: "relative" }}>
      <Typography variant="h6" component="h3">
        {announcement.eventName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {announcement.description}
      </Typography>
    </CardContent> */}
    {hasArrow && (
      <IconButton
        sx={{
          position: "absolute",
          right: 8,
          bottom: 8,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.9)",
          },
        }}
      >
        <NavigateNext />
      </IconButton>
    )}
  </Card>
);

const PageHeader = ({ subheader = "Announcements" }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      padding: "20px",
    }}
  >
    <Typography variant="h6" component="h6" sx={{ color: "#7a7a7a" }}>
      EVENTS & LATEST NEWS
    </Typography>
    <Typography
      variant="h3"
      component="h2"
      sx={{
        color: "#101117",
        fontFamily: "Houschka Rounded Alt",
        fontWeight: "600",
      }}
    >
      {subheader}
    </Typography>
  </Box>
);

//preview 1st 4 announcements
const PreviewAnnouncements = ({
  announcements = [],
  onViewAll,
  handleAnnouncementClick,
}) => {
  const featuredAnnouncement =
    announcements.find((a) => a.featured) || announcements[0];
  const remainingAnnouncements = announcements
    .filter((a) => a !== featuredAnnouncement)
    .slice(0, 3);

  if (announcements.length === 0) {
    return;
  }

  return (
    <Box sx={{ p: 4 }}>
      <PageHeader />
      <Box sx={{ mb: 3 }}>
        {featuredAnnouncement && (
          <PreviewCard
            announcement={featuredAnnouncement}
            onClick={() => handleAnnouncementClick(featuredAnnouncement)}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        {remainingAnnouncements.map((announcement, index) => (
          <Box
            key={index}
            sx={{
              flex: "1 1 calc(33.333% - 16px)",
              minWidth: "250px",
            }}
          >
            <PreviewCard
              announcement={announcement}
              onClick={() => handleAnnouncementClick(announcement)}
            />
          </Box>
        ))}
      </Box>
      {announcements.length > 4 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
            onClick={onViewAll}
          >
            View All Announcements <NavigateNext />
          </Typography>
        </Box>
      )}
    </Box>
  );
};

//All announcements components
const AllAnnouncements = ({
  announcements = [],
  handleAnnouncementClick,
}) => {
  const dispatch = useDispatch();
  return (
    <Box sx={{ p: 4 }}>
      <Breadcrumbs separator="/" sx={{ mb: 4 }}>
        <Link
          underline="hover"
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          color="inherit"
          onClick={() => {
            dispatch(setAnnouncementView("default"));
            dispatch(setIsShowAnnouncements(true));
          }}
        >
          <ArrowBack
            sx={{ mr: 3, fontSize: "14px", fontWeight: "600" }}
            fontSize="small"
          />
          Home
        </Link>
        <Typography
          color="text.primary"
          sx={{ fontSize: "14px", fontWeight: "600", color: "#7a7a7a" }}
        >
          EVENTS & LATEST NEWS
        </Typography>
      </Breadcrumbs>

      <PageHeader />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {announcements.length > 0 ? (
          announcements.map((announcement) => (
            <Box
              key={announcement.id}
              sx={{
                flex: "1 1 calc(25% - 16px)",
                minWidth: "250px",
                // maxWidth: "50%",
              }}
            >
              <AnnouncementCard
                announcement={announcement}
                hasArrow={false}
                onClick={() => {
                  handleAnnouncementClick(announcement);
                }}
              />
            </Box>
          ))
        ) : (
          <Box
            sx={{
              width: "100%",
              p: 4,
              textAlign: "center",
              backgroundColor: "#f5f5f5",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6">No Announcements Available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

//Announcement details component
const IndividualAnnouncementDetails = ({
  announcements = [],
  activeAnnouncement,
}) => {
  const announcement = announcements.find((a) => a.id === activeAnnouncement);
  const dispatch = useDispatch();

  if (!announcement) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Announcement not found!</Typography>
      </Box>
    );
  }

  // Configure DOMPurify to allow certain tags/attributes
  const purifyConfig = {
    ALLOWED_TAGS: [
      "p",
      "b",
      "i",
      "em",
      "strong",
      "a",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "br",
      "div",
      "span",
      "img",
    ],
    ALLOWED_ATTR: ["href", "target", "class", "id", "src", "alt", "title"],
    ALLOW_DATA_ATTR: false,
  };

  return (
    <Box>
      <Breadcrumbs separator="/" sx={{ mb: 2, p: 2 }}>
        <Link
          underline="hover"
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            fontSize: "14px",
          }}
          color="inherit"
          onClick={() => {
            dispatch(setAnnouncementView("default"));
            dispatch(setIsShowAnnouncements(true));
          }}
        >
          <ArrowBack sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          underline="hover"
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            fontSize: "14px",
          }}
          color="inherit"
          onClick={() => {
            dispatch(setAnnouncementView('all'))
          }}
        >
          EVENTS & LATEST NEWS
        </Link>
        <Typography
          color="text.primary"
          sx={{ fontSize: "14px", fontWeight: "600", color: "#7a7a7a" }}
        >
          {announcement.eventName}
        </Typography>
      </Breadcrumbs>

      <PageHeader subheader={announcement.eventName} />
      <Box
        sx={{
          display: "flex",
          gap: 3,
          p: 5,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{ mb: 2, color: "#4a4a4a", fontFamily: "Houschka Rounded Alt" }}
          >
            Details
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(announcement.details, purifyConfig),
              }}
            />
          </Box>
        </Box>

        <Box>
          <CardMedia
            component="img"
            height="400"
            image={getImageURL(announcement.poster)}
            alt={announcement.eventName}
            sx={{ borderRadius: "10px" }}
          />
        </Box>
      </Box>
      {announcement?.howToParticipate && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "#4e84c4",
          }}
        >
          <Box sx={{ padding: "20px 30px" }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{ mb: 2, color: "#fff", fontFamily: "Houschka Rounded Alt" }}
            >
              How to participate?
            </Typography>
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  announcement.participate,
                  purifyConfig
                ),
              }}
            />
          </Box>
          <img
            src={how_to_participate}
            height={"150px"}
            alt="Group of people participating in environmental activities"
            className="w-full h-auto object-contain"
          />
        </Box>
      )}
    </Box>
  );
};

//Main component
const Events = () => {
  // const [activeAnnouncement, setActiveAnnouncement] = useState("");
  const { announcements, announcementView: view, activeAnnouncement } =
    useSelector((state) => state.announcement);
  const dispatch = useDispatch();

  function handleAnnouncementClick(announcement) {
    dispatch(setAnnouncementView("details"))
    dispatch(setActiveAnnouncement(announcement.id))
    dispatch(setIsShowAnnouncements(false));
  }

  return (
    <Box component="section">
      {view === "default" && (
        <PreviewAnnouncements
          announcements={announcements}
          onViewAll={() => dispatch(setAnnouncementView("all"))}
          handleAnnouncementClick={handleAnnouncementClick}
        />
      )}
      {view === "details" && (
        <IndividualAnnouncementDetails
          announcements={announcements}
          activeAnnouncement={activeAnnouncement}
        />
      )}
      {view === "all" && (
        <AllAnnouncements
          announcements={announcements}
          handleAnnouncementClick={handleAnnouncementClick}
        />
      )}
    </Box>
  );
};

export default Events;
